import React from "react";
import { Link } from "react-router-dom";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  formatDisplayDate,
  formatDisplayDateTime,
} from "../../../utils/helperFunctions";
import { QC_STATUS_CHIP } from "../../../utils/constants/index";
import StatusChip from "../../common/StatusChip";
import DirectChip from "../../common/DirectChip";
import GlobalAppBadge from "../../common/AppSwitch/GlobalAppBadge";
import { Button } from "../../ui/button";
import LaunchSummaryChip from "../LaunchSummaryChip";
import ModerationHookDots from "../ModerationHookDots";
import LaunchDateCell from "./LaunchDateCell";
import AutoLaunchCell from "./AutoLaunchCell";
import useAdsetColumnWidth from "../../../hooks/useAdsetColumnWidth";
import { CHECKBOX_COL_WIDTH } from "../../../utils/constants/AdRequest";

const FONT = "'DM Sans', system-ui, sans-serif";

const HEAD_SX = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#4b5563",
  border: "none",
};

const CHECKBOX_SX = {
  padding: "6px",
  color: "#7f7ad5",
  "&.Mui-checked": { color: "#7f7ad5" },
};

const TABLE_SX = {
  minWidth: "max-content",
  fontFamily: FONT,
  "& .MuiTableCell-root": {
    fontFamily: FONT,
    padding: "12px 14px",
    whiteSpace: "nowrap",
  },
  "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableHead-root .MuiTableCell-root": {
    borderLeft: "none",
    borderRight: "none",
    borderTop: "none",
    backgroundColor: "#fff",
  },
  "& .sticky-col": { position: "sticky", zIndex: 2, backgroundColor: "#fff" },
  "& .sticky-check": {
    left: 0,
    width: CHECKBOX_COL_WIDTH,
    minWidth: CHECKBOX_COL_WIDTH,
    maxWidth: CHECKBOX_COL_WIDTH,
  },
  "& .MuiTableCell-root.sticky-check": { padding: "0 0 0 8px" },
  "& .sticky-adset": { boxShadow: "inset -1px 0 0 #e5e7eb" },
  "& .MuiTableHead-root .sticky-col": { zIndex: 3 },
  "& .MuiTableBody-root .MuiTableRow-root:hover .sticky-col": {
    backgroundColor: "#f5f5f5",
  },
  "& .adset-resizer": {
    position: "absolute",
    top: 0,
    right: 0,
    width: "6px",
    height: "100%",
    cursor: "col-resize",
    userSelect: "none",
  },
  "& .adset-resizer:hover": { backgroundColor: "#7f7ad5" },
};

/** A stage the request has not reached yet. */
const NoStage = () => <span className="text-gray-400">—</span>;

const parentId = (r) => (r.ad_id != null && r.ad_id !== -7 ? r.ad_id : "—");

/** Ad Request list table with selection and a resizable Ad Set column. */
const RequestsTable = ({
  requests,
  canMutate,
  canEditLaunchDate,
  showAgencyColumn,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpen,
  onModeration,
  onLaunch,
  onEdit,
}) => {
  const { adsetWidth, startAdsetResize } = useAdsetColumnWidth();
  const adsetLeft = canMutate ? CHECKBOX_COL_WIDTH : 0;
  const adsetSize = {
    left: adsetLeft,
    width: adsetWidth,
    minWidth: adsetWidth,
    maxWidth: adsetWidth,
  };
  const headings = [
    showAgencyColumn ? "Agency/Requester" : "Requester",
    "Priority",
    "Delivery Status",
    "Moderation Status",
    "QC Status",
    "Launch Date",
    "Auto Launch",
    "Launched Status",
  ];

  return (
    <TableContainer sx={{ flex: "1 1 auto", minHeight: 0, overflowX: "auto" }}>
      <Table size="medium" sx={TABLE_SX}>
        <TableHead>
          <TableRow
            sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#fff" }}
          >
            {canMutate && (
              <TableCell className="sticky-col sticky-check" sx={HEAD_SX}>
                <Checkbox
                  checked={
                    requests.length > 0 && selectedIds.size === requests.length
                  }
                  onChange={onToggleAll}
                  sx={CHECKBOX_SX}
                />
              </TableCell>
            )}
            <TableCell
              className="sticky-col sticky-adset"
              sx={{ ...HEAD_SX, ...adsetSize }}
            >
              Ad Set
              <div
                className="adset-resizer"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize the adset name column"
                onMouseDown={startAdsetResize}
                onClick={(e) => e.stopPropagation()}
              />
            </TableCell>
            {headings.map((h) => (
              <TableCell key={h} sx={HEAD_SX}>
                {h}
              </TableCell>
            ))}
            <TableCell sx={{ ...HEAD_SX, width: 100 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.length > 0 ? (
            requests.map((r) => (
              <TableRow
                key={r.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => onOpen(r.id)}
              >
                {canMutate && (
                  <TableCell
                    className="sticky-col sticky-check"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(r.id)}
                      onChange={() => onToggle(r.id)}
                      sx={CHECKBOX_SX}
                    />
                  </TableCell>
                )}
                <TableCell
                  className="sticky-col sticky-adset"
                  sx={{ color: "text.secondary", ...adsetSize }}
                >
                  {r.adset_name ? (
                    <Tooltip
                      title={r.adset_name}
                      arrow
                      placement="bottom-start"
                    >
                      <Link
                        to={`/dashboard/ad-request/${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate text-primaryPurple hover:underline font-medium"
                      >
                        {r.adset_name}
                      </Link>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                  <div className="mt-1.5 flex min-w-0 gap-2">
                    <span className="w-[3px] shrink-0 rounded-full bg-gradient-to-b from-[#7f7ad5] to-[#d9d4f5]" />
                    <div className="min-w-0 space-y-0.5 text-[10px] leading-4 text-gray-400">
                      <div className="flex min-w-0 items-center gap-1">
                        <GlobalAppBadge
                          inline
                          app={r.for_app_name}
                          language={r.language}
                        />
                        {r.requested_via === "agency_direct" && <DirectChip />}
                      </div>
                      <div
                        className="truncate"
                        title={`Created: ${formatDisplayDateTime(r.created_on) ?? "—"}`}
                      >
                        Created{" "}
                        <span className="font-medium text-gray-500">
                          {formatDisplayDateTime(r.created_on) ?? "—"}
                        </span>
                      </div>
                      <div
                        className="truncate"
                        title={`Due: ${formatDisplayDate(r.due_delivery_date) ?? "—"}`}
                      >
                        Due{" "}
                        <span className="font-medium text-gray-500">
                          {formatDisplayDate(r.due_delivery_date) ?? "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  <div className="flex flex-col gap-0.5">
                    {showAgencyColumn && (
                      <span>
                        {r.requested_for_agency_name ||
                          (r.requested_for_agency_id
                            ? `#${r.requested_for_agency_id}`
                            : "—")}
                      </span>
                    )}
                    <span>{r.requested_by_name || "—"}</span>
                    <span className="text-xs text-gray-400">
                      Parent Id: {parentId(r)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <StatusChip subtle status={r.priority} />
                </TableCell>
                <TableCell>
                  <StatusChip
                    subtle
                    status={r.status === "approved" ? "success" : r.status}
                  />
                </TableCell>
                <TableCell>
                  <ModerationHookDots
                    hooks={r.moderation_hooks}
                    onClick={() => onModeration(r)}
                  />
                </TableCell>
                <TableCell>
                  {r.qc_summary ? (
                    <StatusChip
                      subtle
                      status={QC_STATUS_CHIP[r.qc_summary] ?? "pending"}
                    />
                  ) : (
                    <NoStage />
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <LaunchDateCell request={r} canEdit={canEditLaunchDate} />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <AutoLaunchCell request={r} canEdit={canEditLaunchDate} />
                </TableCell>
                <TableCell>
                  <LaunchSummaryChip
                    summary={r.launch_summary}
                    onClick={() => onLaunch(r)}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => onOpen(r.id)}
                    >
                      View
                    </Button>
                    {canMutate && r.status === "pending" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => onEdit(r)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={canMutate ? 11 : 10}
                align="center"
                sx={{ py: 6, color: "text.secondary" }}
              >
                No ad requests found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RequestsTable;
